import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import axios from '../../utils/axios';
import { usePreferences } from '../../context/PreferencesContext';

const Header = () => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const { theme, setTheme, isDarkMode } = usePreferences();
  const location = useLocation();
  const navigate = useNavigate();

  // User state
  const [user, setUser] = useState(() => {
    return JSON.parse(sessionStorage.getItem("user") || "{}");
  });

  useEffect(() => {
    const syncUser = () => {
      const updated = JSON.parse(sessionStorage.getItem("user") || "{}");
      setUser(updated);
    };

    window.addEventListener("userUpdated", syncUser);
    return () => {
      window.removeEventListener("userUpdated", syncUser);
    };
  }, []);

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Modal states
  const [showAllModal, setShowAllModal] = useState(false);
  const [allModalFilter, setAllModalFilter] = useState('all');
  const [allModalSearch, setAllModalSearch] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchNotifications = useCallback(async () => {
    const currentUserId = user?.id || JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    if (!currentUserId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      const res = await axios.get('/notifications', {
        params: { user_id: currentUserId }
      });
      if (res.data?.success) {
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    const handleUpdate = () => fetchNotifications();
    window.addEventListener('notificationUpdated', handleUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationUpdated', handleUpdate);
    };
  }, [fetchNotifications]);

  const handleNotificationClickItem = async (notification) => {
    const currentUserId = user?.id || JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    try {
      if (!notification.is_read) {
        await axios.patch(`/notifications/${notification.id}/read`, {
          user_id: currentUserId
        });
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    } catch (err) {
      console.error('Error marking notification read:', err);
    }

    setSelectedNotification({ ...notification, is_read: true });
    setShowDetailModal(true);
    setShowNotifications(false);
  };

  const handleMarkAllRead = async () => {
    const currentUserId = user?.id || JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    try {
      await axios.post('/notifications/mark-all-read', {
        user_id: currentUserId
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all your notifications?')) {
      return;
    }
    const currentUserId = user?.id || JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    try {
      await axios.delete('/notifications', {
        data: { user_id: currentUserId }
      });
      setNotifications([]);
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent('notificationUpdated'));
    } catch (err) {
      console.error('Error clearing notifications:', err);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e?.stopPropagation();
    const currentUserId = user?.id || JSON.parse(sessionStorage.getItem("user") || "{}")?.id;
    try {
      await axios.delete(`/notifications/${notificationId}`, {
        data: { user_id: currentUserId }
      });
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === notificationId);
        if (target && !target.is_read) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n.id !== notificationId);
      });
      if (selectedNotification?.id === notificationId) {
        setShowDetailModal(false);
        setSelectedNotification(null);
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNavigateAction = (url) => {
    if (!url) return;
    setShowDetailModal(false);
    setShowAllModal(false);
    setShowNotifications(false);
    navigate(url);
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'just now';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMin = Math.floor((now - date) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };

  const getTypeIconName = (type) => {
    switch (type) {
      case 'error':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'success':
        return 'CheckCircle';
      case 'info':
      default:
        return 'Info';
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'error':
        return {
          iconColor: 'text-rose-600',
          bgBadge: 'bg-rose-50 text-rose-700 border-rose-200',
          dot: 'bg-rose-500',
        };
      case 'warning':
        return {
          iconColor: 'text-amber-600',
          bgBadge: 'bg-amber-50 text-amber-700 border-amber-200',
          dot: 'bg-amber-500',
        };
      case 'success':
        return {
          iconColor: 'text-emerald-600',
          bgBadge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
          dot: 'bg-emerald-500',
        };
      case 'info':
      default:
        return {
          iconColor: 'text-blue-600',
          bgBadge: 'bg-blue-50 text-blue-700 border-blue-200',
          dot: 'bg-blue-500',
        };
    }
  };

  const getCategoryLabel = (cat) => {
    if (!cat) return 'General';
    const map = {
      asset_assignment: 'Asset Assignment',
      assignment: 'Assignment',
      lifecycle: 'Asset Lifecycle',
      audit: 'Audit Workflow',
      warranty: 'Warranty',
      security: 'Security Alert',
      system: 'System Notification',
    };
    return map[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const filteredAllNotifications = notifications.filter((item) => {
    if (allModalFilter === 'unread' && item.is_read) {
      return false;
    }
    if (allModalFilter === 'asset_assignment' && item.category !== 'asset_assignment' && item.category !== 'assignment') {
      return false;
    }
    if (allModalFilter === 'lifecycle' && item.category !== 'lifecycle') {
      return false;
    }
    if (allModalFilter === 'audit' && item.category !== 'audit') {
      return false;
    }
    if (allModalFilter === 'security_system' && !['security', 'system', 'login', 'alert'].includes(item.category)) {
      return false;
    }

    if (allModalSearch?.trim()) {
      const q = allModalSearch.toLowerCase();
      const matchTitle = item.title?.toLowerCase()?.includes(q);
      const matchMessage = item.message?.toLowerCase()?.includes(q);
      const matchCategory = item.category?.toLowerCase()?.includes(q);
      return matchTitle || matchMessage || matchCategory;
    }

    return true;
  });

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      console.log('Searching for:', searchQuery);
    }
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
    setShowNotifications(false);
  };

  const handleLogout = async () => {
    try {
      const token = sessionStorage.getItem("token");
      await fetch(
        `${import.meta.env.VITE_API_URL}/logout`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
    } catch (error) {
      console.log(error);
    }

    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    setShowProfileMenu(false);
    navigate("/login");
  };

  const handleNavigateToProfile = () => {
    navigate('/profile-settings');
    setShowProfileMenu(false);
  };

  const handleNavigateToSecurity = () => {
    navigate('/security-settings');
    setShowProfileMenu(false);
  };

  const handleNavigateToPreferences = () => {
    navigate('/preferences');
    setShowProfileMenu(false);
  };

  const handleNavigateToHelp = () => {
    navigate('/help-support');
    setShowProfileMenu(false);
  };

  const isAuthenticated = !!sessionStorage.getItem("token");
  const userName = user?.name || "Guest User";
  const userEmail = user?.email || "guest@example.com";
  const userRole = user?.role?.name || "User";
  return (
    <>
    <header 
      className="fixed top-0 left-0 right-0 z-40 shadow-subtle bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 transition-colors"
    >
      <div className="flex items-center justify-between h-16 px-6">
        {/* Empty div for spacing - logo removed from header */}
        <div className="w-8" />

        {/* Search Section */}
        <div className="flex-1 max-w-md mx-8">
          <form onSubmit={handleSearch} className="relative">
            <div className={`relative transition-smooth ${isSearchFocused ? 'ring-2 ring-blue-500/20' : ''} rounded-md`}>
              <Input
                type="search"
                placeholder="Search assets, locations, or IDs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className="pl-10 pr-4"
              />
              <Icon
                name="Search"
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>
          </form>
        </div>

        {/* Actions Section */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Appearance / Theme Selector */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setShowThemeMenu(!showThemeMenu);
                setShowNotifications(false);
                setShowProfileMenu(false);
              }}
              className="relative text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={`Appearance: ${theme ? theme.charAt(0).toUpperCase() + theme.slice(1) : 'System'} (Click to change)`}
            >
              <Icon
                name={theme === 'dark' ? 'Moon' : theme === 'light' ? 'Sun' : 'Monitor'}
                size={19}
                className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
              />
            </Button>

            {showThemeMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowThemeMenu(false)} 
                />
                <div className="absolute right-0 top-12 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden py-1.5 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-100 dark:border-gray-700/60 mb-1">
                    <Icon name="Palette" size={12} className="text-primary" />
                    <span>Appearance Theme</span>
                  </div>
                  {[
                    { id: 'light', label: 'Light Mode', icon: 'Sun' },
                    { id: 'dark', label: 'Dark Mode', icon: 'Moon' },
                    { id: 'system', label: 'System (Auto)', icon: 'Monitor' }
                  ].map((item) => {
                    const isSelected = (theme || 'system') === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setTheme(item.id);
                          setShowThemeMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-xs flex items-center justify-between text-left transition-colors ${
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Icon name={item.icon} size={15} className={isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                          <span>{item.label}</span>
                        </span>
                        {isSelected && (
                          <Icon name="Check" size={14} className="text-blue-600 dark:text-blue-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Notifications */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNotificationClick}
              className="relative text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Notifications"
            >
              <Icon name="Bell" size={20} className={unreadCount > 0 ? "text-primary" : "text-gray-600"} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>

            {showNotifications && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowNotifications(false)} 
                />
                <div className="absolute right-0 top-12 w-88 sm:w-96 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="p-3.5 border-b border-border bg-muted/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm text-foreground">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary hover:underline font-medium"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-border/50">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
                        <Icon name="BellOff" size={24} className="text-gray-300" />
                        <span>No notifications yet.</span>
                      </div>
                    ) : (
                      notifications.slice(0, 8).map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClickItem(notification)}
                          className={`p-3.5 hover:bg-muted/40 transition-colors cursor-pointer flex items-start gap-3 ${
                            !notification.is_read ? 'bg-primary/5' : ''
                          }`}
                        >
                          <div
                            className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${
                              notification.type === 'error'
                                ? 'bg-rose-500'
                                : notification.type === 'warning'
                                ? 'bg-amber-500'
                                : notification.type === 'success'
                                ? 'bg-emerald-500'
                                : 'bg-blue-500'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className={`text-xs text-foreground truncate ${!notification.is_read ? 'font-bold' : 'font-semibold'}`}>
                                {notification.title}
                              </p>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {formatTimeAgo(notification.created_at)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="mt-1.5 flex items-center justify-between gap-2">
                              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                                {getCategoryLabel(notification.category)}
                              </span>
                              {!notification.is_read && (
                                <span className="text-[10px] text-primary font-semibold flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                  Unread
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-2.5 border-t border-border bg-muted/20 flex flex-col gap-1.5">
                    <button
                      onClick={() => {
                        setShowNotifications(false);
                        setShowAllModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2 px-3 text-xs font-semibold text-primary hover:bg-primary/10 rounded-lg transition-colors border border-primary/20 bg-primary/5"
                    >
                      <Icon name="List" size={15} />
                      <span>View All Notifications ({notifications.length})</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/asset-management-dashboard?tab=lifecycle');
                        setShowNotifications(false);
                      }}
                      className="text-[11px] text-center font-medium text-muted-foreground hover:text-foreground transition-colors pt-0.5"
                    >
                      View Asset Lifecycle & Clearance →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Profile Menu */}
    
          <div className="relative">
            <Button
              variant="ghost"
              onClick={handleProfileClick}
              className="flex items-center space-x-3 px-3"
            >
              {/* Avatar */}
              <div className="relative">
                <div className="w-9 h-9 bg-blue-600 rounded-full overflow-hidden flex items-center justify-center text-white font-semibold uppercase">
                {(user?.profile_image_url || user?.profile_image) ? (
                 <img
                 src={user.profile_image_url || user.profile_image}
                 className="w-full h-full object-cover"
                 onError={(e) => {
                 e.target.style.display = "none";
                 }}
                />
               ) : (
               userName
               ?.split(" ")
               ?.map(word => word.charAt(0))
               ?.join("")
               ?.substring(0, 2)
               ?.toUpperCase()
              )}
             </div>

                {/* Status Dot */}
                <span
                  className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                    isAuthenticated
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                />
              </div>

              {/* User Info */}
              <div className="hidden md:block text-left leading-tight">
                <p className="text-sm font-semibold text-gray-900">
                  {userName}
                </p>

                <p className="text-xs text-gray-500 truncate max-w-[180px]">
                  {userEmail}
                </p>
              </div>

              <Icon
                name="ChevronDown"
                size={16}
                className="text-gray-500"
              />
            </Button>

            {/* Dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 top-12 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">

                {/* Top User Info */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">

                  <div className="flex items-center gap-3">

                    <div className="relative">

                      <div className="w-12 h-12 bg-blue-600 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-lg uppercase">
                      {(user?.profile_image_url || user?.profile_image) ? (
                       <img
                        src={user.profile_image_url || user.profile_image}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                        e.target.style.display = "none";
                       }}
                     />
                     ) : (
                  userName
                  ?.split(" ")
                  ?.map(word => word.charAt(0))
                  ?.join("")
                  ?.substring(0, 2)
                  ?.toUpperCase()
                  )}
                </div>

                      <span
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                          isAuthenticated
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      />

                    </div>

                    <div className="flex-1 min-w-0">

                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {userName}
                      </h4>

                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {userEmail}
                      </p>

                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                        {userRole}
                      </p>

                    </div>

                  </div>

                </div>

                {/* Quick Appearance Switcher in Profile Menu */}
                <div className="p-2.5 border-b border-gray-100 dark:border-gray-700 bg-muted/20">
                  <div className="flex items-center justify-between text-[11px] font-semibold text-muted-foreground mb-1.5 px-1">
                    <span>Appearance</span>
                    <span className="capitalize font-bold text-primary">{theme || 'system'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1 bg-muted/50 p-1 rounded-lg">
                    {[
                      { id: 'light', label: 'Light', icon: 'Sun' },
                      { id: 'dark', label: 'Dark', icon: 'Moon' },
                      { id: 'system', label: 'Auto', icon: 'Monitor' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setTheme(opt.id)}
                        className={`flex items-center justify-center gap-1 py-1 rounded text-xs transition-colors ${
                          (theme || 'system') === opt.id
                            ? 'bg-white dark:bg-gray-700 text-primary shadow-xs font-semibold'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        title={opt.label}
                      >
                        <Icon name={opt.icon} size={13} />
                        <span className="text-[10px]">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">

                  <button
                    onClick={handleNavigateToProfile}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center gap-2"
                  >
                    <Icon name="User" size={16} />
                    <span>Profile Settings</span>
                  </button>

                  <button
                    onClick={handleNavigateToSecurity}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center gap-2"
                  >
                    <Icon name="Shield" size={16} />
                    <span>Security</span>
                  </button>

                  <button
                    onClick={handleNavigateToPreferences}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center gap-2"
                  >
                    <Icon name="Settings" size={16} />
                    <span>Preferences & Appearance</span>
                  </button>

                  <button
                    onClick={handleNavigateToHelp}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center gap-2"
                  >
                    <Icon name="HelpCircle" size={16} />
                    <span>Help & Support</span>
                  </button>

                </div>

                {/* Logout */}
                <div className="border-t border-gray-100 dark:border-gray-700 py-2">

                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 flex items-center gap-2"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Sign Out</span>
                  </button>

                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </header>

    {/* ======================================================== */}
    {/* MODAL 1: VIEW ALL NOTIFICATIONS POPUP                    */}
    {/* ======================================================== */}
    {showAllModal && (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => setShowAllModal(false)}
      >
        <div 
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-3xl flex flex-col max-h-[88vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/70 dark:bg-gray-850">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <Icon name="Bell" size={20} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">All Notifications</h2>
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {notifications.length} Total
                  </span>
                  {unreadCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
                      {unreadCount} Unread
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Showing alerts for logged in user ({userName})
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs font-medium text-primary hover:bg-primary/10 px-2.5 sm:px-3 py-1.5 rounded-lg border border-primary/20 transition-colors flex items-center gap-1.5"
                  title="Mark all as read"
                >
                  <Icon name="CheckCheck" size={14} />
                  <span className="hidden sm:inline">Mark all read</span>
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-xs font-medium text-rose-600 hover:bg-rose-50 px-2.5 sm:px-3 py-1.5 rounded-lg border border-rose-200 transition-colors flex items-center gap-1.5"
                  title="Clear all notifications"
                >
                  <Icon name="Trash2" size={14} />
                  <span className="hidden sm:inline">Clear all</span>
                </button>
              )}
              <button
                onClick={() => setShowAllModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="p-3 sm:px-5 sm:py-3 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 space-y-2.5">
            <div className="relative">
              <Input
                type="search"
                placeholder="Search notifications by title, message, or category..."
                value={allModalSearch}
                onChange={(e) => setAllModalSearch(e.target.value)}
                className="pl-9 pr-8 text-xs py-2 w-full"
              />
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              {allModalSearch && (
                <button
                  onClick={() => setAllModalSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs no-scrollbar">
              {[
                { id: 'all', label: 'All', count: notifications.length },
                { id: 'unread', label: 'Unread', count: unreadCount },
                { id: 'asset_assignment', label: 'Asset Assignment' },
                { id: 'lifecycle', label: 'Lifecycle' },
                { id: 'audit', label: 'Audit' },
                { id: 'security_system', label: 'System & Security' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setAllModalFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-full font-medium shrink-0 transition-colors flex items-center gap-1.5 ${
                    allModalFilter === tab.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <span>{tab.label}</span>
                  {typeof tab.count === 'number' && (
                    <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                      allModalFilter === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800 p-3 sm:p-5 space-y-2">
            {filteredAllNotifications.length === 0 ? (
              <div className="py-16 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mb-3">
                  <Icon name="BellOff" size={26} />
                </div>
                <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">No notifications found</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                  {allModalSearch
                    ? `No notifications matching "${allModalSearch}"`
                    : allModalFilter === 'unread'
                    ? "You're all caught up! No unread notifications."
                    : "No notifications available in this category."}
                </p>
              </div>
            ) : (
              filteredAllNotifications.map((notification) => {
                const styles = getTypeStyles(notification.type);
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClickItem(notification)}
                    className={`p-3.5 sm:p-4 rounded-xl border transition-all cursor-pointer flex items-start gap-3.5 hover:shadow-sm ${
                      !notification.is_read
                        ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                        : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    {/* Type Icon Badge */}
                    <div className={`w-9 h-9 rounded-lg shrink-0 flex items-center justify-center border ${styles.bgBadge}`}>
                      <Icon name={getTypeIconName(notification.type)} size={18} className={styles.iconColor} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                            {getCategoryLabel(notification.category)}
                          </span>
                          {!notification.is_read && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                              Unread
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0" title={formatFullDate(notification.created_at)}>
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>

                      <h4 className={`text-sm mt-1 text-gray-900 dark:text-gray-100 ${!notification.is_read ? 'font-bold' : 'font-semibold'}`}>
                        {notification.title}
                      </h4>

                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed">
                        {notification.message}
                      </p>

                      <div className="mt-2.5 flex items-center justify-between text-xs pt-1">
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                          {formatFullDate(notification.created_at)}
                        </span>
                        <div className="flex items-center gap-2">
                          {notification.action_url && (
                            <span className="text-[11px] text-primary font-medium flex items-center gap-1 hover:underline">
                              View details <Icon name="ArrowRight" size={12} />
                            </span>
                          )}
                          <button
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                            className="text-gray-400 hover:text-rose-600 p-1 rounded transition-colors"
                            title="Delete notification"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Modal Footer */}
          <div className="p-3.5 sm:px-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-850 flex items-center justify-between text-xs text-gray-500">
            <span>Showing {filteredAllNotifications.length} of {notifications.length} notifications</span>
            <Button variant="outline" size="sm" onClick={() => setShowAllModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )}

    {/* ======================================================== */}
    {/* MODAL 2: NOTIFICATION DETAIL POPUP                       */}
    {/* ======================================================== */}
    {showDetailModal && selectedNotification && (
      <div 
        className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => setShowDetailModal(false)}
      >
        <div 
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/70 dark:bg-gray-850">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${getTypeStyles(selectedNotification.type).bgBadge}`}>
                <Icon name={getTypeIconName(selectedNotification.type)} size={20} className={getTypeStyles(selectedNotification.type).iconColor} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {getCategoryLabel(selectedNotification.category)}
                  </span>
                  <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400">
                    Read
                  </span>
                </div>
                <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 block">
                  {formatFullDate(selectedNotification.created_at)} ({formatTimeAgo(selectedNotification.created_at)})
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowDetailModal(false)}
              className="w-8 h-8 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-6 overflow-y-auto space-y-4">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 leading-snug">
                {selectedNotification.title}
              </h3>
            </div>

            {/* Message body container */}
            <div className="bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
              {selectedNotification.message}
            </div>

            {/* Metadata info cards if present */}
            {selectedNotification.metadata && typeof selectedNotification.metadata === 'object' && Object.keys(selectedNotification.metadata).length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700/60 space-y-2">
                <h5 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Details</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {Object.entries(selectedNotification.metadata).map(([key, val]) => (
                    <div key={key} className="flex flex-col bg-white dark:bg-gray-800 p-2 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-[10px] text-gray-400 uppercase font-semibold">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate mt-0.5">
                        {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Button */}
            {selectedNotification.action_url && (
              <div className="pt-2">
                <Button
                  variant="default"
                  onClick={() => handleNavigateAction(selectedNotification.action_url)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 shadow-sm"
                >
                  <span>Open Related Page / Asset</span>
                  <Icon name="ArrowRight" size={16} />
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3.5 sm:px-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-850 flex items-center justify-between">
            <button
              onClick={(e) => handleDeleteNotification(selectedNotification.id, e)}
              className="text-xs text-rose-600 hover:text-rose-700 font-medium flex items-center gap-1.5 px-2 py-1 rounded hover:bg-rose-50 transition-colors"
            >
              <Icon name="Trash2" size={14} />
              <span>Delete Notification</span>
            </button>
            <Button variant="outline" size="sm" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;